// useLocations — location data access for LocationSelector. The continent→country tree is bundled
// (synchronous); states + cities are fetched via TanStack Query so every geo row shares one cache
// (refetching states for the same country across rows is a cache hit, not a new request).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { locationService } from '@/services/api';
import type { LocationData } from '@/types';
import type { SelectOption } from '@/components/ui/Select/Select';

export function useLocations() {
  const data: LocationData = useMemo(() => locationService.getLocationData(), []);

  // name → code map (states API keys by country code, but rows carry the display name).
  const nameToCode = useMemo(() => {
    const m = new Map<string, string>();
    Object.values(data.continentCountries).forEach((list) =>
      list.forEach((c) => m.set(c.name, c.code)),
    );
    return m;
  }, [data]);

  const continentOptions: SelectOption[] = useMemo(
    () => data.continents.map((c) => ({ value: c, label: c })),
    [data],
  );

  /** Countries for a continent (or all, suggested-first) as Select options keyed by name. */
  const countriesFor = useMemo(
    () =>
      (continent?: string): SelectOption[] => {
        const lists = continent
          ? [data.continentCountries[continent] ?? []]
          : Object.values(data.continentCountries);
        const opts = lists
          .flat()
          .map((c) => ({ value: c.name, label: c.name, suggested: c.isSuggested }));

        // Pin United States at the top of the country list
        const usIdx = opts.findIndex(
          (o) => o.value === 'United States' || o.value === 'US' || o.label === 'United States',
        );
        if (usIdx > 0) {
          const [us] = opts.splice(usIdx, 1);
          opts.unshift(us);
        }
        return opts;
      },
    [data],
  );

  const codeForCountry = (name: string): string | undefined => nameToCode.get(name);

  return { data, continentOptions, countriesFor, codeForCountry };
}

/** States for a country code (shared cache across rows). */
export function useStates(countryCode: string | undefined) {
  return useQuery({
    queryKey: ['locations', 'states', countryCode],
    queryFn: () => locationService.getStates(countryCode!),
    enabled: !!countryCode,
    staleTime: Infinity,
  });
}

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Cities for the selected state. `q` is what the user has typed; suggestions follow that text. */
export function useCities(state: string | undefined, countryCode?: string, q?: string) {
  const debounced = useDebounced(q ?? '', 200);
  return useQuery({
    queryKey: ['locations', 'cities', state ?? '', countryCode ?? '', debounced],
    queryFn: () => locationService.getCities(state!, countryCode, debounced),
    enabled: !!state,
    staleTime: 5 * 60 * 1000,
  });
}

/** Regions for a country code (shared cache across rows). Empty for countries without a grouping. */
export function useRegions(countryCode: string | undefined) {
  return useQuery({
    queryKey: ['locations', 'regions', countryCode],
    queryFn: () => locationService.getRegions(countryCode!),
    enabled: !!countryCode,
    staleTime: Infinity,
  });
}

/** Search box over continents, countries, regions, states and cities (2+ characters). */
export function useLocationSearch(q: string) {
  const debounced = useDebounced(q.trim(), 250);
  return useQuery({
    queryKey: ['locations', 'search', debounced.toLowerCase()],
    queryFn: () => locationService.search(debounced),
    enabled: debounced.length >= 2,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });
}
